import { FileText, RefreshCw, type LucideIcon } from 'lucide-react';
import type { BasecampAssignment } from '@/types/basecamp';
import type { ExtensionMenuContext, ExtensionMenuItem } from '@/types/integration';
import type { EntityContextMenuEntry } from '@/components/features/entity-context-menu';
import { OpenDestinationIcons } from '@/lib/open-destination-icons';
import { panelSearchMenuAction } from '@/lib/panel-search-menu';
import { basecampEntityMenus } from './basecamp-entity-menus';

const emptyMenuCtx: ExtensionMenuContext = {};

export type TodosRowWorkSpecStatus = { status: string; filePath: string } | null;

function extensionItemsForTodo(todo: BasecampAssignment): ExtensionMenuItem[] {
  const decl = basecampEntityMenus.find((d) => d.entityType === 'todo');
  return decl ? decl.items(todo, emptyMenuCtx) : [];
}

function resolveDisabled(
  disabled: ExtensionMenuItem['disabled'],
  todo: BasecampAssignment
): boolean | undefined {
  if (disabled == null) return undefined;
  if (typeof disabled === 'function') return disabled(todo, emptyMenuCtx);
  return disabled;
}

function mapExtensionItem(
  item: ExtensionMenuItem,
  todo: BasecampAssignment,
  close: () => void
): EntityContextMenuEntry[] {
  const entries: EntityContextMenuEntry[] = [];
  if (item.separatorBefore) entries.push({ separator: true });
  entries.push({
    label: item.label,
    icon: item.icon as LucideIcon | undefined,
    disabled: resolveDisabled(item.disabled, todo),
    onSelect: () => {
      item.action(todo, emptyMenuCtx);
      close();
    },
  });
  return entries;
}

function hasWorkSpecFile(workSpecStatus: TodosRowWorkSpecStatus): boolean {
  return !!(workSpecStatus && workSpecStatus.status !== 'none' && workSpecStatus.filePath);
}

/**
 * Shared Basecamp todos-row item source (ADR-0020).
 * Right-click and row `...` must call this builder.
 */
export function buildTodosRowContextMenuEntries(options: {
  todo: BasecampAssignment;
  onPreview?: () => void;
  onSearch?: () => void;
  onRefresh?: () => void;
  /** When set (including `null` = loaded, no spec), include View work spec / No work spec. */
  workSpecStatus?: TodosRowWorkSpecStatus;
  onViewWorkSpec?: () => void;
  close: () => void;
}): EntityContextMenuEntry[] {
  const { todo, onPreview, onSearch, onRefresh, workSpecStatus, onViewWorkSpec, close } = options;
  const entries: EntityContextMenuEntry[] = [];

  if (onPreview) {
    entries.push({
      label: 'Preview',
      icon: OpenDestinationIcons.preview,
      onSelect: () => {
        onPreview();
        close();
      },
    });
  }

  entries.push(...extensionItemsForTodo(todo).flatMap((item) => mapExtensionItem(item, todo, close)));

  if (workSpecStatus !== undefined) {
    const hasSpec = hasWorkSpecFile(workSpecStatus);
    entries.push({ separator: true });
    entries.push({
      label: hasSpec ? 'View work spec' : 'No work spec',
      icon: FileText,
      disabled: !hasSpec,
      onSelect: () => {
        if (!hasSpec) return;
        if (onViewWorkSpec) onViewWorkSpec();
        else onPreview?.();
        close();
      },
    });
  }

  if (onSearch || onRefresh) {
    entries.push({ separator: true });
  }
  if (onSearch) {
    entries.push(
      panelSearchMenuAction(() => {
        onSearch();
        close();
      }),
    );
  }
  if (onRefresh) {
    entries.push({
      label: 'Refresh row',
      icon: RefreshCw,
      onSelect: () => {
        onRefresh();
        close();
      },
    });
  }

  return entries;
}
